import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import Dashboard from './components/Dashboard';
import PublicLeaderboard from './components/PublicLeaderboard';
import PublicProductivitySheet from './components/PublicProductivitySheet';
import AppUpdateGuide from './components/AppUpdateGuide';
import { managerAPI } from './config/api';
import APP_COLOURS from './config/colors';
import { useTheme } from './theme';
import './styles/main.css';
import './App.css';

function App() {
  useTheme(); // re-render tree when light/dark scheme changes
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignup, setShowSignup] = useState(false);

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const token = localStorage.getItem('traq_access_token');
        const storedUserData = localStorage.getItem('traq_user_data');
        
        if (token && storedUserData) {
          const parsedUserData = JSON.parse(storedUserData);
          setIsAuthenticated(true);
          setUserData(parsedUserData);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // Clear invalid data
        localStorage.removeItem('traq_access_token');
        localStorage.removeItem('traq_user_data');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleLoginSuccess = (data) => {
    setIsAuthenticated(true);
    setUserData(data);
    setShowSignup(false);
  };

  const handleSignupSuccess = (data) => {
    // After successful signup, switch to login page
    setShowSignup(false);
    // Optionally could auto-login the user here if the API returns login data
  };

  const handleLogout = () => {
    // Fire logout activity log before clearing the token (fire-and-forget)
    if (managerAPI.logout) {
      managerAPI.logout().catch(() => {});
    }
    setIsAuthenticated(false);
    setUserData(null);
    setShowSignup(false);
    localStorage.removeItem('traq_access_token');
    localStorage.removeItem('traq_user_data');
  };

  const switchToSignup = () => {
    setShowSignup(true);
  };

  const switchToLogin = () => {
    setShowSignup(false);
  };

  if (loading) {
    return (
      <div className="App">
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: APP_COLOURS.BG }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
            <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Leaderboard Route */}
          <Route path="/leaderboard/:token" element={<PublicLeaderboard />} />
          
          {/* Public Productivity Sheet Route */}
          <Route path="/productivity-sheet/:token" element={<PublicProductivitySheet />} />
          
          {/* Public App Update Guide Route */}
          <Route path="/app-update-guide" element={<AppUpdateGuide />} />
          
          {/* Main App Routes */}
          <Route 
            path="/*" 
            element={
              isAuthenticated && userData ? (
                <Dashboard userData={userData} onLogout={handleLogout} />
              ) : showSignup ? (
                <SignupPage 
                  onSignupSuccess={handleSignupSuccess} 
                  onSwitchToLogin={switchToLogin} 
                />
              ) : (
                <LoginPage 
                  onLoginSuccess={handleLoginSuccess} 
                  onSwitchToSignup={switchToSignup}
                />
              )
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
